import { useState, useEffect, lazy, Suspense } from "react";
import React from "react";
import { useParams } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import { CircularProgress, Box } from "@mui/material";
import { KEY } from "../utils/ChartUtils";
import LazyRender from "../utils/LazyRender";
import InfoCard from "../InfoCard";
import STHitterPercentiles from "./STHitterPercentiles";
import STPitcherPercentiles from "./STPitcherPercentiles";
import STStatCard from "./STStatCard";
import STBattedBallProfile from "./STBattedBallProfile";
import STSprayChart from "./STSprayChart";
import STSwingChart from "./STSwingChart";
import STArsenalProfile from "./STArsenalProfile";
import STInfoCardTemp from "./STInfoCardTemp";

const ReleaseProfile = lazy(() => import("../ReleaseProfile"));
const MovementProfile = lazy(() => import("../MovementProfile"));
const PitchHeatmap = lazy(() => import("../PitchHeatmap"));
const PitchSplits = lazy(() => import("../PitchSplits"));


function reducePitchData(data: any[]) {
  const resultMap = new Map();

  data.forEach((item: any) => {
    // Use pitch_type as the key since we want one entry per pitch type
    const key = item.pitch_type;

    if (!resultMap.has(key)) {
      // Initialize with both left and right percentages set to 0
      resultMap.set(key, {
        pitchName: item.pitch_name,
        pitchType: item.pitch_type,
        leftPercent: 0,
        rightPercent: 0
      });
    }

    // Update the appropriate percentage based on batter handedness
    const pitchData = resultMap.get(key);
    if (item.stand === 'L') {
      pitchData.leftPercent = -Math.abs(item.percentage); // Ensure negative for lefties
    } else {
      pitchData.rightPercent = Math.abs(item.percentage); // Ensure positive for righties
    }
  });

  return Array.from(resultMap.values());
}


export default function STPlayerPage(props: any) {
  const [playerData, setPlayerData] = useState<any>({});
  const [playerInfo, setPlayerInfo] = useState<any>({});
  const { id } = useParams();

  const [pitchData, setPitchData] = useState<any[]>([]);
  const [pitches, setPitches] = useState<any[]>([]);
  const [armAngle, setArmAngle] = useState<Number>(0);
  const [splitData, setSplitData] = useState<any[]>([]);

  const [seasons, setSeasons] = useState<string[]>(["2026"]);
  const [season, setSeason] = useState<string>("2026");



  useEffect(() => {
    fetch(
      props.prod
        ? "https://oriolebird.pythonanywhere.com/st/player/" + id
        : "/st/player/" + id
    )
      .then((res) => res.json())
      .then((data1) => {
        //console.log("D1: ", data1);
        let tempSeason = season
        setSeasons(Object.keys(data1))
        if (!Object.keys(data1).includes(season)) {
          tempSeason = Object.keys(data1)[0]
          setSeason(tempSeason)
          setPlayerData(data1[tempSeason]);
        }
        else {
          setPlayerData(data1[season])
        }
        fetch(
          props.prod
            ? `https://oriolebird.pythonanywhere.com/player-info/`
            : "/player-info/",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              playerUrlProp: data1[tempSeason]["UPURL"],
            }),
          }
        )
          .then((res) => res.json())
          .then((data2) => {
            setPlayerInfo(data2);
            let height = 0
            if(Object.keys(data2).includes("common") && Object.keys(data2.common).includes("playerInfo") && Object.keys(data2.common.playerInfo).includes("Height")){
              height = data2.common.playerInfo.Height
            }
            //console.log("d2: ", data2)
            if (data1[tempSeason].Position === "P")
              fetch(
                props.prod
                  ? "https://oriolebird.pythonanywhere.com/st/pitches/" + id + "/" + tempSeason + "/" + height
                  : "/st/pitches/" + id + "/" + tempSeason + "/" + height
              )
                .then((res) => res.json())
                .then((data3) => {
                  //console.log("D3: ", data3);
                  setPitchData(data3)
                  const p = Object.keys(data3.reduce((r: any, { pitch_type }: any) => { r[pitch_type] = ''; return r; }, {})).map((pitch: string) => KEY[pitch])
                  //console.log("Pitches: ", p.toString())
                  setPitches(p)
                  const spl = reducePitchData(data3)
                  //console.log("SPL", spl)
                  setSplitData(spl)
                  //console.log(data3)
                  setArmAngle(data3[0].avg_arm_angle.toFixed(1))
                })
          })
      })

  }, [id, props.prod, season]);
  console.log("PD:", playerData);
  console.log("pi:", playerInfo);
  //console.log(percentiles)
  return (
    <div>

      {playerData !== undefined && playerData !== null && (
        <div style={{ marginBottom: "10px" }}>
          <title>{playerData.batter_name !== undefined || playerData.pitcher_name !== undefined ? (playerData.pitcher_name ? playerData.pitcher_name +" - Prospect Savant" : playerData.batter_name + " - Prospect Savant") : "Prospect Savant"}</title>

          <Grid container justifyContent="center" spacing={2} maxWidth="100%">
            <Grid item maxWidth="90vw" >
              <Paper
                sx={{
                  height: "100%",
                  maxWidth: "90vw",
                  width: 500,
                  border: "#293241 solid 2px",
                  backgroundColor: "#FFFFFF",
                  boxShadow:
                    "1px 1px 1px black,2px 2px 1px white,3px 3px 1px black,4px 4px 3px",
                }}
                square
                variant="outlined"
              >
                {playerInfo.common && ("common" in playerInfo || "Error" in playerInfo) &&  <InfoCard playerInfoProp={playerInfo} idProp={id} />}
                {"Error" in playerInfo &&  <STInfoCardTemp playerDataProp={playerData} idProp={id} />}
              </Paper>
            </Grid>
            {playerData.Position === "P" && ("common" in playerInfo || "Error" in playerInfo) && (
              <Grid item maxWidth="90vw">
                <STPitcherPercentiles id={id} playerDataProp={playerData} prod={props.prod} isDesktop={props.isDesktop} season={season} setSeason={setSeason} seasons={seasons}/>
              </Grid>
            )}
            {playerData.Position === "P" && ("common" in playerInfo || "Error" in playerInfo) && (
              <Grid item maxWidth="90vw" >
                <STStatCard playerDataProp={playerData}
                      playerInfoProp={playerInfo}
                      isDesktop={props.isDesktop}
                      season={season}/>
              </Grid>
            )}
            {playerData.Position === "P" && ("common" in playerInfo || "Error" in playerInfo) && pitchData.length !== 0 && (
              <Grid item maxWidth="90vw" >
                <STArsenalProfile playerDataProp={playerData}
                      playerInfoProp={playerInfo}
                      isDesktop={props.isDesktop}
                      season={season}
                      id={id}
                      prod={props.prod}/>
                      
              </Grid>
            )}
            {playerData.Position === "P" && ("common" in playerInfo || "Error" in playerInfo) && pitchData.length !== 0 && (
              <LazyRender>
                <Suspense fallback={
                  <Grid item maxWidth="90vw">
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                      <CircularProgress />
                    </Box>
                  </Grid>
                }>
                  <PitchSplits
                    isDesktop={props.isDesktop}
                    id={id}
                    playerDataProp={playerData}
                    prod={props.prod}
                    height={playerInfo.common ? playerInfo.common.playerInfo.Height : null}
                    playerInfoProp={playerInfo}
                    splitData={splitData}
                  />
                </Suspense>
              </LazyRender>
            )}
            {playerData.Position === "P" && ("common" in playerInfo || "Error" in playerInfo) && pitchData.length !== 0 && (
              <LazyRender>
                <Suspense fallback={
                  <Grid item maxWidth="90vw">
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                      <CircularProgress />
                    </Box>
                  </Grid>
                }>
                  <MovementProfile
                    isDesktop={props.isDesktop}
                    id={id}
                    playerDataProp={playerData}
                    pitchData={pitchData}
                    prod={props.prod}
                    playerInfoProp={playerInfo}
                    pitches={pitches}
                  />
                </Suspense>
              </LazyRender>
            )}
            {playerData.Position === "P" && ("common" in playerInfo || "Error" in playerInfo) && pitchData.length !== 0 && (
              <LazyRender>
                <Suspense fallback={
                  <Grid item maxWidth="90vw">
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                      <CircularProgress />
                    </Box>
                  </Grid>
                }>
                  <PitchHeatmap
                    isDesktop={props.isDesktop}
                    id={id}
                    playerDataProp={playerData}
                    pitchData={pitchData}
                    prod={props.prod}
                    playerInfoProp={playerInfo}
                    pitches={pitches}
                  />
                </Suspense>
              </LazyRender>
            )}
            {playerData.Position === "P" && ("common" in playerInfo || "Error" in playerInfo) && pitchData.length !== 0 && (
              <LazyRender>
                <Suspense fallback={
                  <Grid item maxWidth="90vw">
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                      <CircularProgress />
                    </Box>
                  </Grid>
                }>
                  <ReleaseProfile
                    isDesktop={props.isDesktop}
                    id={id}
                    prod={props.prod}
                    playerDataProp={playerData}
                    height={"Error" in playerInfo ? 76 : playerInfo.common.playerInfo.Height}
                    playerInfoProp={playerInfo}
                    pitchData={pitchData}
                    pitches={pitches}
                    armAngle={armAngle}
                  />
                </Suspense>
              </LazyRender>
            )}
            {playerData.Position !== "P"  && ("common" in playerInfo || "Error" in playerInfo) && (
              <Grid item maxWidth="90vw" >
                <STHitterPercentiles id={id} playerDataProp={playerData} prod={props.prod} isDesktop={props.isDesktop} season={season} setSeason={setSeason} seasons={seasons}/>
              </Grid>
              
            )}
            {playerData.Position !== "P" && ("common" in playerInfo || "Error" in playerInfo) && (
              <Grid item maxWidth="90vw" >
                <STStatCard playerDataProp={playerData}
                      playerInfoProp={playerInfo}
                      isDesktop={props.isDesktop}
                      season={season}
                      />
                <STBattedBallProfile
                  isDesktop={props.isDesktop}
                  season={season}
                  id={id}
                  prod={props.prod}
                  playerInfoProp={playerInfo}
                  playerData={playerData}
                />
              </Grid>
              
            )}
            {playerData.Position !== "P" && ("common" in playerInfo || "Error" in playerInfo) && (
              <Grid item maxWidth="90vw">
                <STSprayChart
                  isDesktop={props.isDesktop}
                  season={season}
                  id={id}
                  prod={props.prod}
                  playerInfoProp={playerInfo}
                  playerDataProp={playerData}
                />
              </Grid>
            )}
            {playerData.Position !== "P" && ("common" in playerInfo || "Error" in playerInfo) && (
              <Grid item maxWidth="90vw">
                <STSwingChart
                  isDesktop={props.isDesktop}
                  season={season}
                  id={id}
                  prod={props.prod}
                  playerInfoProp={playerInfo}
                  playerDataProp={playerData}
                />
              </Grid>
            )}
          </Grid>
        </div>
      )}
    </div>
  );
}
